import React, { Fragment, useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { clearCart } from '../redux/cart';

function ConfirmOrderPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const API_URL = process.env.REACT_APP_API_URL;

    const [loading, setLoading] = useState(false); // Spinner state

    // Destructure with safe defaults so hooks below aren't conditional
    const { cartItems = [], finalPrice = 0, address = {}, mobile, selectedOffer } = location.state || {};

    // Redirect effect if state missing
    useEffect(() => {
        if (!location.state) {
            toast.error('Order details missing. Redirecting...');
            const t = setTimeout(() => navigate('/'), 1800);
            return () => clearTimeout(t);
        }
    }, [location.state, navigate]);

    // Derive original total & savings for professional summary
    const { originalTotal, savings } = useMemo(() => {
        const originalTotalCalc = cartItems.reduce((acc, i) => acc + (i.mrp ? i.mrp * i.quantity : i.price * i.quantity), 0);
        const charged = finalPrice;
        return { originalTotal: originalTotalCalc, savings: Math.max(0, originalTotalCalc - charged) };
    }, [cartItems, finalPrice]);

    const formattedAddress = useMemo(() => {
        if (!address) return '';
        const parts = [address.name, address.house, address.area, address.landmark, address.city].filter(Boolean);
        return parts.join(', ');
    }, [address]);

    const offerLabel = useMemo(() => {
        if (!selectedOffer) return '';
        const { code, name, unit, value } = selectedOffer;
        if (code) return code;
        if (name) return name;
        if (value) {
            if (unit === 'Percent') return `${value}% OFF`;
            return `₹${value} OFF`;
        }
        return '';
    }, [selectedOffer]);

    const handlePlaceOrder = async () => {
        setLoading(true); // Start spinner
        try {
            const response = await axios.post(`${API_URL}/place-order`, { cartItems, finalPrice, address, mobile, appliedOffer: selectedOffer });
            toast.success(response.data.message);
            setTimeout(() => {
                dispatch(clearCart());
                navigate('/my-orders', { replace: true });
            }, 2000);
        } catch (err) {
            console.error("Error placing order:", err);
            toast.error("Failed to place order. Try again later.");
        } finally {
            setLoading(false); // Stop spinner
        }
    };

    // If state missing, render minimal container (toast already queued)
    if (!location.state) {
        return (
            <Fragment>
                <div className='container-fluid m-t25'>
                    <div className='row'>
                        <div className='col-12 small text-muted'>Redirecting...</div>
                    </div>
                </div>
                <ToastContainer />
            </Fragment>
        );
    }

    return (
        <Fragment>
            <div className='container-fluid m-t25'>
                <div className='row'>
                    <div className='col-12 cat-strip border shadow-sm'>
                        <div>
                            <button onClick={() => navigate(-1)}><IoMdArrowRoundBack /></button>
                            <label>ORDER SUMMARY</label>
                        </div>
                    </div>
                </div>
            </div>
            <div className='container-fluid'>
                <div className='row'>
                    <div className='col-xl-10 col-lg-11 col-md-12 col-12 mx-auto'>
                        <div className='order-summary-card mt-3 mb-3'>
                            {/* <div className='osc-header'>Order Summary</div> */}
                            <div className='osc-section'>
                                <div className='osc-row'><span>Items</span><span>{cartItems.length}</span></div>
                                <div className='osc-row'><span>Subtotal (MRP)</span><span>₹{originalTotal}</span></div>
                                {savings > 0 && <div className='osc-row saving'><span>You Save</span><span>-₹{savings}</span></div>}
                                {offerLabel && <div className='osc-row offer'><span>Offer Applied</span><span>{offerLabel}</span></div>}
                                <div className='osc-row total'><span>Payable Amount</span><span>₹{finalPrice}</span></div>
                            </div>
                            <div className='osc-divider'></div>
                            <div className='osc-section'>
                                <div className='osc-subtitle'>Delivery Address</div>
                                <div className='osc-address'>{formattedAddress}</div>
                            </div>
                            <div className='osc-section pt-0'>
                                <div className='osc-notice'>Review details before placing your order. Once shipped, cancellation may not be possible.</div>
                            </div>
                            <div className='osc-divider'></div>
                            <div className='osc-section items-list'>
                                <div className='osc-subtitle mb-2'>Items</div>
                                <table className='osc-items-table'>
                                    <thead>
                                        <tr>
                                            <th style={{width:'55%'}}>Product</th>
                                            <th>Qty</th>
                                            <th>Price</th>
                                            <th>Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cartItems.map((it, idx) => (
                                            <tr key={idx}>
                                                <td>{it.name || it.title}</td>
                                                <td>{it.quantity}</td>
                                                <td>₹{it.price}</td>
                                                <td>₹{it.price * it.quantity}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <button className='checkout-btn place-order-btn' onClick={handlePlaceOrder} disabled={loading}>
                            {loading ? <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true' /> : 'CONFIRM & PLACE ORDER'}
                        </button>
                    </div>
                </div>
            </div>
            <div className='mt-5 pt-5 pb-5 mb-5'></div>
            <ToastContainer />
        </Fragment>
    );
}

export default ConfirmOrderPage;
